import React from 'react';
import { parseUtcTimestamp } from '../../Helpers/utils';

/**
 * Base Audit Trail Tooltip Component
 * Provides consistent styling and structure for all audit trail tooltips
 */
const AuditTrailTooltip = ({ 
  title, 
  children, 
  auditEntries = [], 
  isLoading = false,
  maxWidth = '400px' 
}) => {
  
  // Show loading state
  if (isLoading) {
    return (
      <div style={{
        backgroundColor: '#333',
        color: '#fff',
        padding: '12px',
        borderRadius: '4px',
        fontSize: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}>
        <div style={{
          width: '16px',
          height: '16px',
          border: '2px solid #fff',
          borderTopColor: 'transparent',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
        <span>Loading history...</span>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Format the timestamp
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = parseUtcTimestamp(timestamp);
      if (!date) return 'N/A';
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return 'N/A';
    }
  };

  const getActionText = (entryType) => {
    const type = entryType?.toUpperCase();
    switch (type) {
      case 'CREATION':
        return 'Created';
      case 'EDIT':
        return 'Edited';
      case 'COMMENT':
        return 'Added a comment';
      default:
        return 'Performed an action';
    }
  };

  // Get recent audit trail entries (limit to 3, oldest first)
  const recentEntries = auditEntries && auditEntries.length > 0
    ? [...auditEntries]
        .sort((a, b) => (parseUtcTimestamp(a.timestamp) ?? new Date(0)) - (parseUtcTimestamp(b.timestamp) ?? new Date(0)))
        .slice(-3)
    : [];

  return (
    <div style={{
      backgroundColor: '#333',
      color: '#fff',
      padding: '12px',
      borderRadius: '4px',
      fontSize: '12px',
      maxWidth,
      lineHeight: '1.6',
      zIndex: 9999,
    }}>
      {/* Title */}
      {title && (
        <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '13px' }}>
          {title}
        </div>
      )}
      
      {/* Custom content (formula, reference accounts, child stats, etc.) */}
      {children}
      
      {/* Audit Trail Section */}
      {recentEntries.length > 0 ? (
        <>
          <div style={{ 
            borderTop: '1px solid #555', 
            marginTop: '12px', 
            paddingTop: '12px',
            marginBottom: '8px',
            fontWeight: 'bold'
          }}>
            Recent History
          </div>
          {recentEntries.map((entry, index) => {
            const firstName = entry.user?.first_name || '';
            const lastName = entry.user?.last_name || '';
            const userName = `${firstName} ${lastName}`.trim() || 'Unknown User';
            const timestamp = formatDate(entry.timestamp);
            const actionText = getActionText(entry.entry_type);
            
            return (
              <div 
                key={entry.id || index} 
                style={{ 
                  marginBottom: index < recentEntries.length - 1 ? '12px' : '0',
                  paddingBottom: index < recentEntries.length - 1 ? '12px' : '0',
                  borderBottom: index < recentEntries.length - 1 ? '1px solid #444' : 'none',
                }}
              >
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ fontWeight: 'bold', color: '#4fc3f7' }}>{userName}</span>
                  <span style={{ color: '#aaa', marginLeft: '8px', fontSize: '11px' }}>{timestamp}</span>
                </div>
                <div style={{ color: '#ddd', marginBottom: entry.comment_text ? '4px' : '0' }}>
                  {actionText}
                </div>
                {entry.comment_text && (
                  <div style={{ 
                    color: '#fff', 
                    fontStyle: 'italic',
                    backgroundColor: '#444',
                    padding: '6px 8px',
                    borderRadius: '3px',
                    marginTop: '4px'
                  }}>
                    "{entry.comment_text}"
                  </div>
                )}
              </div>
            );
          })}
        </>
      ) : (
        !children && (
          <div style={{ color: '#aaa', fontStyle: 'italic', marginTop: title ? '8px' : '0' }}>
            No history yet.
          </div>
        )
      )}
    </div>
  );
};

export default AuditTrailTooltip;
